import { useCallback, useEffect, useState } from 'react';
import { authManager } from 'src/utils/auth';

interface ApiRequestState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  isUnauthorized: boolean;
  hasFetched: boolean;
}

interface UseApiRequestOptions {
  requestKey: string;
  autoFetch?: boolean;
  dependencies?: any[];
}

export const useApiRequest = <T>(
  apiCall: () => Promise<T>,
  options: UseApiRequestOptions,
): ApiRequestState<T> & { refetch: () => Promise<void> } => {
  const { requestKey, autoFetch = true, dependencies = [] } = options;

  const [state, setState] = useState<ApiRequestState<T>>({
    data: null,
    isLoading: false,
    error: null,
    isUnauthorized: false,
    hasFetched: false,
  });

  // Subscribe to global unauthorized state
  useEffect(() => {
    const unsubscribe = authManager.onUnauthorized(() => {
      setState((prev) => ({ ...prev, isUnauthorized: true, isLoading: false }));
    });
    return unsubscribe;
  }, []);

  const executeRequest = useCallback(async () => {
    // Check if we can make the request
    if (!authManager.canMakeRequest(requestKey)) {
      if (authManager.isCurrentlyUnauthorized()) {
        setState((prev) => ({ ...prev, isUnauthorized: true }));
      }
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await apiCall();
      setState((prev) => ({
        ...prev,
        data: result,
        isLoading: false,
        error: null,
        hasFetched: true,
      }));
      authManager.completeRequest(requestKey);
    } catch (error: any) {
      const wasUnauthorized = authManager.handleError(error, requestKey);

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: wasUnauthorized ? 'Unauthorized' : error.message || 'An error occurred',
        isUnauthorized: wasUnauthorized,
        hasFetched: !wasUnauthorized, // Don't mark as fetched if unauthorized
      }));
    }
  }, [apiCall, requestKey]);

  // Auto-fetch on mount and dependency changes
  useEffect(() => {
    if (autoFetch && !state.hasFetched && !state.isUnauthorized) {
      executeRequest();
    }
  }, [autoFetch, executeRequest, state.hasFetched, state.isUnauthorized, ...dependencies]);

  return {
    ...state,
    refetch: executeRequest,
  };
};
